import React from 'react';
import { Alert } from 'react-bootstrap';
import {
  formatDeadlineCountdown,
  formatPeriodRangeLabel,
  formatReportTagDeadline,
  getDeadlineUrgency,
  type DeadlineUrgency,
} from '@features/report/tag/utils/reportTagDeadline';

type Props = {
  since?: string | null;
  until?: string | null;
  deadline?: string | null;
  /** When false, still show period+deadline lines without urgency colors if deadline exists. */
  tagIsOpen?: boolean;
  className?: string;
};

/**
 * Soft deadline status strip. Omits entirely when deadline is null (do not advertise missing).
 */
export default function DeadlineStatusBanner({
  since,
  until,
  deadline,
  tagIsOpen = true,
  className = 'mb-2',
}: Props) {
  if (!deadline) return null;

  const periodLabel = formatPeriodRangeLabel(since, until);
  const deadlineLabel = formatReportTagDeadline(deadline);
  const countdown = formatDeadlineCountdown(deadline);
  const { urgency } = getDeadlineUrgency(deadline);

  const variant: DeadlineUrgency =
    tagIsOpen && urgency ? urgency : 'info';

  const title =
    tagIsOpen && countdown
      ? urgency === 'warning' && countdown.startsWith('Due')
        ? `Deadline approaching · ${countdown}`
        : countdown
      : deadlineLabel
        ? `Deadline: ${deadlineLabel}`
        : null;

  if (!title && !periodLabel) return null;

  return (
    <Alert variant={variant} className={`py-2 small ${className}`.trim()}>
      {title ? <div className="fw-semibold">{title}</div> : null}
      <div className="text-body-secondary">
        {periodLabel ? <>Period: {periodLabel}</> : null}
        {periodLabel && deadlineLabel ? ' · ' : null}
        {deadlineLabel ? <>Deadline: {deadlineLabel}</> : null}
      </div>
      {tagIsOpen && urgency === 'danger' ? (
        <div className="mt-1">
          You can still submit until this reporting period is closed.
        </div>
      ) : null}
    </Alert>
  );
}
